import React, { useState, useMemo } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Icon,
  Spinner,
  Center,
  Box,
} from '@chakra-ui/react';
import { MdEdit } from 'react-icons/md';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { useIMask } from 'react-imask';
import { useNavigate } from 'react-router-dom';

interface Secretaria {
  codSecretaria: number;
  dscSecretaria: string;
  numAtivo: number;
}

interface TabelaSecretariaProps {
  listaSecretaria: Secretaria[];
  listaVazia: string;
  loading: boolean;
}

type Ordenacao = { chave: keyof Secretaria; direcao: 'asc' | 'desc' } | null;

const TabelaSecretaria = ({ listaSecretaria, listaVazia, loading }: TabelaSecretariaProps) => {
  const navigate = useNavigate();
  const [ordenacao, setOrdenacao] = useState<Ordenacao>(null);
  const [colunaDestacada, setColunaDestacada] = useState<keyof Secretaria | null>(null);

  const manipularOrdenacao = (chave: keyof Secretaria) => {
    let direcao: 'asc' | 'desc' = 'asc';
    if (ordenacao && ordenacao.chave === chave && ordenacao.direcao === 'asc') {
      direcao = 'desc';
    }
    setOrdenacao({ chave, direcao });
  };

  const listaOrdenada = useMemo(() => {
    const copia = [...listaSecretaria];
    if (ordenacao !== null) {
      copia.sort((a, b) => {
        const valorA = a[ordenacao.chave];
        const valorB = b[ordenacao.chave];
        if (typeof valorA === 'string' && typeof valorB === 'string') {
          const comparacao = valorA.localeCompare(valorB);
          return ordenacao.direcao === 'asc' ? comparacao : -comparacao;
        }
        if (valorA < valorB) return ordenacao.direcao === 'asc' ? -1 : 1;
        if (valorA > valorB) return ordenacao.direcao === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return copia;
  }, [listaSecretaria, ordenacao]);

  const obterIndicadorOrdenacao = (chave: keyof Secretaria) => {
    if (ordenacao && ordenacao.chave === chave) {
      return ordenacao.direcao === 'asc' ? (
        <FaCaretUp color="#1789FC" style={{ marginLeft: '4px' }} />
      ) : (
        <FaCaretDown color="#1789FC" style={{ marginLeft: '4px' }} />
      );
    }
      return <FaCaretUp color="#4a5568" style={{ marginLeft: '4px' }} />;
  };

  const renderRows = () =>
    listaOrdenada.length > 0 ? (
      listaOrdenada.map((secretaria) => (
        <Tr key={secretaria.codSecretaria}>
          <Td>{secretaria.codSecretaria}</Td>
          <Td>{secretaria.dscSecretaria}</Td>
          <Td>{secretaria.numAtivo === 1 ? 'Ativo' : 'Inativo'}</Td>
          {/* <Td>{secretaria.datRegistro.split('-').reverse().join('/')}</Td>
          <Td>{secretaria.datInativo ? secretaria.datInativo.split('-').reverse().join('/') : 'Não Inativado'}</Td> */}
          <Td>
            <IconButton
              aria-label="Botão Editar"
              bgColor="#fff"
              onClick={() => navigate(`editar-secretaria/${secretaria.codSecretaria}`)}
            >
              <Icon as={MdEdit} fontSize="24px" color="#1789FC" />
            </IconButton>
          </Td>
        </Tr>
      ))
    ) : (
      <Tr>
        <Td colSpan={6} color="#999898" textAlign="center">
          {listaVazia}
        </Td>
      </Tr>
    );

  return (
    <TableContainer>
      <Table variant="simple">
        <Thead>
          <Tr>
          <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('codSecretaria')}
              onMouseEnter={() => setColunaDestacada('codSecretaria')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box
                display="flex"
                alignItems="center"
                color={ordenacao?.chave === 'codSecretaria' ? 'blue.500' : 'inherit'}
              >
                Código{obterIndicadorOrdenacao('codSecretaria')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('dscSecretaria')}
              onMouseEnter={() => setColunaDestacada('dscSecretaria')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'dscSecretaria' ? 'blue.500' : 'inherit'}>
                Descrição{obterIndicadorOrdenacao('dscSecretaria')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('numAtivo')}
              onMouseEnter={() => setColunaDestacada('numAtivo')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'numAtivo' ? 'blue.500' : 'inherit'}>
                Situação{obterIndicadorOrdenacao('numAtivo')}
              </Box>
            </Th>
            <Th>Editar</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loading ? (
            <Tr>
              <Td colSpan={6}>
                <Center>
                  <Spinner size="lg" color="blue.500" />
                </Center>
              </Td>
            </Tr>
          ) : (
            renderRows()
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default TabelaSecretaria;
